import { z } from "zod";
import { AgentEvalScoreDetailSchema, RecordSetComparisonSchema } from "./eval-score-detail";

/**
 * Graders: an agent of kind `grader` plus a one-to-one `graders` row holding a
 * grader prompt, an ordered rubric of labelled scores and a pass threshold. Its run
 * writes one `grades` row per subject. Shapes shared by the chokepoint, the API
 * and the dashboard live here; the tables are documented in
 * packages/db/tables/graders.md and grades.md.
 */

export const GRADER_KINDS = ["thread", "verifier"] as const;
export type GraderKind = (typeof GRADER_KINDS)[number];

export const GRADER_STATUSES = ["enabled", "paused", "archived"] as const;
export type GraderStatus = (typeof GRADER_STATUSES)[number];

export const GRADE_TRIGGERS = ["sampled", "scheduled", "on_demand", "backfill"] as const;
export type GradeTrigger = (typeof GRADE_TRIGGERS)[number];

export const GRADE_STATUSES = ["queued", "running", "graded", "skipped", "failed"] as const;
export type GradeStatus = (typeof GRADE_STATUSES)[number];

/** An issue stays open until a person or an accepted suggestion closes it, or triage merges it into another. */
/** The worker job that files one failing grade into its grader's issues. */
export const GRADE_TRIAGE_JOB_TYPE = "grade.triage";

export type GradeTriageJob = {
  jobType: typeof GRADE_TRIAGE_JOB_TYPE;
  workspacePublicId: string;
  gradePublicId: string;
};

export const ISSUE_STATUSES = ["open", "fixed", "wont_fix", "duplicate"] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];
/** What a person may set; only issue triage marks an issue a duplicate, by merging it. */
export const ISSUE_SETTABLE_STATUSES = ["open", "fixed", "wont_fix"] as const;
export type IssueSettableStatus = (typeof ISSUE_SETTABLE_STATUSES)[number];
/** Who opened an issue; `grader` is issue triage after a failing grade. */
export const ISSUE_OPENED_BY = ["person", "improver", "grader"] as const;
export type IssueOpenedBy = (typeof ISSUE_OPENED_BY)[number];

/** A suggestion's lifecycle; only a person moves it to accepted or declined. */
export const SUGGESTION_STATUSES = [
  "queued",
  "running",
  "suggested",
  "no_change",
  "accepted",
  "declined",
  "superseded",
  "failed",
] as const;
export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number];

export const GraderRubricLineSchema = z
  .object({
    label: z.string().min(1).max(200),
    score: z.number().min(0).max(1),
  })
  .strict();
export type GraderRubricLine = z.infer<typeof GraderRubricLineSchema>;

/** Ordered; the grader picks exactly one line. Labels must be unique. */
export const GraderRubricSchema = z
  .array(GraderRubricLineSchema)
  .min(2)
  .max(8)
  .refine((lines) => new Set(lines.map((l) => l.label)).size === lines.length, {
    message: "Rubric labels must be unique.",
  });
export type GraderRubric = z.infer<typeof GraderRubricSchema>;

/** A step the grader relied on, by public ids so it renders without a join. */
export const GradeEvidenceSchema = z
  .object({
    thread_id: z.string().min(1),
    step: z.number().int().positive(),
    tool_call_id: z.string().min(1).max(256).optional(),
  })
  .strict();
export type GradeEvidence = z.infer<typeof GradeEvidenceSchema>;

/**
 * A verifier's diff, in one of the two shapes agent eval results already use.
 * No free-form member: a grader that cannot produce one of these skips.
 */
export const GradeDetailSchema = z.discriminatedUnion("kind", [
  RecordSetComparisonSchema,
  AgentEvalScoreDetailSchema,
]);
export type GradeDetail = z.infer<typeof GradeDetailSchema>;
